import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiService } from "../services/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";


const toKey = (value, index) => {
    const raw = String(value || "").trim();
    if (!raw) {
        return `field_${index}`;
    }
    return raw
        .replace(/\s+/g, "_")
        .replace(/[^A-Za-z0-9_]/g, "")
        .replace(/^\d+/, "")
        || `field_${index}`;
};

const normalizeTime = (value) => {
    const raw = String(value || "").trim();
    if (!raw) {
        return "";
    }
    const match = raw.match(/(\d{1,2})[:.](\d{2})/);
    if (!match) {
        return "";
    }
    return `${String(Number(match[1])).padStart(2, "0")}:${match[2]}`;
};

const extractArray = (value, seen = new Set()) => {
    if (!value || typeof value !== "object" || seen.has(value)) {
        return [];
    }
    seen.add(value);
    if (Array.isArray(value)) {
        return value;
    }
    const candidates = ["attributesBody", "attributes", "raeume", "items", "data", "list", "options", "werte"];
    for (const key of candidates) {
        if (Array.isArray(value[key])) {
            return value[key];
        }
    }
    for (const nested of Object.values(value)) {
        if (Array.isArray(nested)) {
            return nested;
        }
        const fromNested = extractArray(nested, seen);
        if (fromNested.length > 0) {
            return fromNested;
        }
    }
    return [];
};

const mapOptions = (attribute) => {
    const raw =
        attribute?.options
        || attribute?.werte
        || attribute?.values
        || attribute?.possibleValues
        || attribute?.auspraegungen
        || [];
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((item, index) => {
        if (typeof item === "string" || typeof item === "number") {
            return { value: String(item), label: String(item) };
        }
        if (item && typeof item === "object") {
            const value =
                item.value
                ?? item.id
                ?? item.code
                ?? item.key
                ?? item.schluessel
                ?? item.bezeichnung
                ?? item.label
                ?? index;
            const label =
                item.label
                ?? item.bezeichnung
                ?? item.name
                ?? item.title
                ?? item.text
                ?? value;
            return {
                value: String(value),
                label: String(label),
            };
        }
        return { value: String(index), label: `Option ${index + 1}` };
    });
};

const guessFieldType = (attribute, options) => {
    const typeHint = String(attribute?.type || attribute?.inputType || attribute?.renderType || "").toLowerCase();
    const labelHint = String(attribute?.label || attribute?.bezeichnung || attribute?.name || "").toLowerCase();
    if (typeHint.includes("date") || labelHint.includes("datum")) {
        return "date";
    }
    if (typeHint.includes("time") || labelHint.includes("uhr")) {
        return "time";
    }
    if (options.length > 0) {
        return options.length <= 5 ? "radio" : "select";
    }
    if (labelHint.includes("kosten") || labelHint.includes("betrag") || labelHint.includes("euro")) {
        return "currency";
    }
    if (labelHint.includes("besucher") || labelHint.includes("anzahl")) {
        return "number";
    }
    return "text";
};

const normalizeAttributes = (tarifResponse) => {
    // Vom Backend normalisiert (GET /tarif/{id}: attributesBody mit description/type/selection.options).
    if (Array.isArray(tarifResponse?.kubu_attributes)) {
        return tarifResponse.kubu_attributes.map((attribute) => ({
            key: attribute.id,
            sourceKey: attribute.id,
            label: attribute.label,
            info: attribute.info,
            options: attribute.options || [],
            required: true,
            type: attribute.input === "select" && (attribute.options || []).length <= 5 ? "radio" : attribute.input,
            defaultValue: attribute.default,
            raw: attribute,
        }));
    }
    const rawAttributes = extractArray(tarifResponse?.attributesBody || tarifResponse);
    return rawAttributes.map((attribute, index) => {
        const key = toKey(
            attribute?.key
            || attribute?.name
            || attribute?.id
            || attribute?.merkmalId
            || attribute?.code,
            index,
        );
        const label =
            attribute?.label
            || attribute?.bezeichnung
            || attribute?.name
            || attribute?.title
            || key;
        const options = mapOptions(attribute);
        const sourceKey = [attribute?.key, attribute?.name, attribute?.id, attribute?.merkmalId, attribute?.code]
            .find((value) => value !== undefined && value !== null && String(value).trim() !== "");
        return {
            key,
            sourceKey: sourceKey !== undefined ? String(sourceKey) : key,
            label,
            options,
            required: Boolean(attribute?.required || attribute?.pflichtfeld),
            type: guessFieldType(attribute, options),
            raw: attribute,
        };
    });
};

const normalizeRaeume = (raeumeResponse) => {
    const items = extractArray(raeumeResponse);
    return items.map((item, index) => {
        if (typeof item === "string") {
            return { id: item, name: item, raw: item };
        }
        const id =
            item?.id
            ?? item?.raumId
            ?? item?.nutzungsortId
            ?? item?.key
            ?? item?.value
            ?? `raum_${index}`;
        const name =
            item?.bezeichnung
            ?? item?.name
            ?? item?.title
            ?? item?.label
            ?? String(id);
        return {
            id: String(id),
            name: String(name),
            raw: item,
        };
    });
};

const formatMoney = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return "-";
    }
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(amount);
};

const extractPreispositionen = (calculationResponse) => {
    const preisliste = calculationResponse?.einzellizenz?.preispositionen;
    if (Array.isArray(preisliste)) {
        return preisliste;
    }
    return [];
};

// Antwort von tarif/einzellizenz: preispositionen [{bezeichnung, wert, typKennung}], Summe = typKennung "BRUT".
const resolveBruttoGesamt = (calculationResponse) => {
    const positionen = extractPreispositionen(calculationResponse);
    const brutto = positionen.find((position) => position?.typKennung === "BRUT");
    return brutto ? brutto.wert : null;
};

// Bestellung freigegeben für Anmeldung, Nachmeldung und Voranmeldung (13.10.2026).
const GEMA_ORDER_ENABLED = true;

// Nur genutzt, falls die Tarif-Antwort keine Merkmale liefert. Feldnamen vorläufig (nicht per HAR verifiziert).
const FALLBACK_FIELDS = [
    { key: "raumgroesseQm", sourceKey: "raumgroesseQm", label: "Raumgröße (qm)", type: "number", options: [] },
    { key: "eintrittNetto", sourceKey: "eintrittNetto", label: "Eintritt netto (€)", type: "currency", options: [] },
    { key: "pausenOhneMusik", sourceKey: "pausenOhneMusik", label: "Pausen ohne Musik (Minuten)", type: "number", options: [] },
    {
        key: "sponsoring",
        sourceKey: "sponsoring",
        label: "Sponsoring",
        type: "radio",
        options: [{ value: "false", label: "nein" }, { value: "true", label: "ja" }],
    },
    {
        key: "zusaetzlicheTvWiedergaben",
        sourceKey: "zusaetzlicheTvWiedergaben",
        label: "Zusätzliche TV-Wiedergaben",
        type: "radio",
        options: [{ value: "false", label: "nein" }, { value: "true", label: "ja" }],
    },
];


const templateKey = (template) => (template ? `${template.lizenz_id}/${template.position || ""}` : "");

const formatDateDe = (value) => {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : (value || "-");
};

const pickMerkmalValue = (merkmale, attribute) => {
    if (!merkmale || typeof merkmale !== "object") {
        return undefined;
    }
    const candidates = [attribute.sourceKey, attribute.key, attribute.raw?.key, attribute.raw?.name, attribute.raw?.id];
    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null && merkmale[candidate] !== undefined && merkmale[candidate] !== null) {
            const value = merkmale[candidate];
            return typeof value === "object" ? (value.value ?? value.wert ?? JSON.stringify(value)) : String(value);
        }
    }
    return undefined;
};

const AttributeInput = ({ attribute, value, onChange }) => {
    const common = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm";
    if (attribute.type === "radio" && attribute.options.length > 0) {
        return (
            <div className="flex flex-wrap gap-4">
                {attribute.options.map((option) => (
                    <label key={`${attribute.key}_${option.value}`} className="inline-flex items-center gap-2 text-sm">
                        <input
                            type="radio"
                            name={attribute.key}
                            value={option.value}
                            checked={String(value || "") === String(option.value)}
                            onChange={(event) => onChange(event.target.value)}
                        />
                        {option.label}
                    </label>
                ))}
            </div>
        );
    }
    if (attribute.type === "select") {
        return (
            <select value={value || ""} onChange={(event) => onChange(event.target.value)} className={common}>
                <option value="">Bitte wählen</option>
                {attribute.options.map((option) => (
                    <option key={`${attribute.key}_${option.value}`} value={option.value}>{option.label}</option>
                ))}
            </select>
        );
    }
    const inputType = attribute.type === "currency" ? "number" : attribute.type;
    return (
        <input
            type={["date", "time", "number"].includes(inputType) ? inputType : "text"}
            step={attribute.type === "currency" ? "0.01" : undefined}
            value={value || ""}
            onChange={(event) => onChange(event.target.value)}
            className={common}
        />
    );
};

const GemaSubmissionWizard = ({ open, onOpenChange, selectedEvent, onSubmitted }) => {
    const [phase, setPhase] = useState(1);
    const [loadingTarif, setLoadingTarif] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [calcError, setCalcError] = useState("");
    const [templatesState, setTemplatesState] = useState({ loading: false, error: "", candidates: [], suggestion: null, reason: null });
    const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
    const [copyLoading, setCopyLoading] = useState(false);
    const [templatePrefill, setTemplatePrefill] = useState(null);
    const [draftTarifId, setDraftTarifId] = useState("");
    const [draftNutzungsortId, setDraftNutzungsortId] = useState("");
    const [appliedIds, setAppliedIds] = useState({ tarifId: "", nutzungsortId: "" });
    const [eventName, setEventName] = useState("");
    const [tarifResponse, setTarifResponse] = useState(null);
    const [raeumeResponse, setRaeumeResponse] = useState(null);
    const [selectedRaumIds, setSelectedRaumIds] = useState([]);
    const [formData, setFormData] = useState({ beginDate: "", startTime: "", endTime: "" });
    // Herkunft je Merkmal: kubu | vorlage | standard | fehlt
    const [prefillSource, setPrefillSource] = useState({});
    const [calculationResponse, setCalculationResponse] = useState(null);
    const [contact, setContact] = useState(null);
    const [contactError, setContactError] = useState("");
    // Portal: "Anzahl Auftritte" = Anzahl der Setlists (eine je Band/Act). Fragt das KuBu-Formular nicht ab.
    const [anzahlSetlists, setAnzahlSetlists] = useState("");
    const [tarifSuche, setTarifSuche] = useState({ query: "", loading: false, error: "", results: null, defaultOrt: "" });
    const [meldenPreview, setMeldenPreview] = useState(null);
    const [meldenPreviewError, setMeldenPreviewError] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingRule, setSavingRule] = useState(false);

    // Echte Portal-IDs nur aus dem GEMA-Abgleich bzw. einer gespeicherten Anmeldung - nie aus dem
    // internen Tarif-Kürzel (gema_tarif_vorschlag wie "U-K").
    const eventIds = useMemo(() => ({
        tarifId: selectedEvent?.external?.tarif_id || selectedEvent?.local_gema?.tarif_id || "",
        nutzungsortId: selectedEvent?.external?.nutzungsort_id || selectedEvent?.local_gema?.nutzungsort_id || "",
    }), [selectedEvent]);
    const hasEventIds = Boolean(eventIds.tarifId && eventIds.nutzungsortId);

    const needsSetlists = Boolean(tarifResponse?.setlistSupported);
    const tarifAttributes = useMemo(() => normalizeAttributes(tarifResponse), [tarifResponse]);
    const usesFallbackFields = Boolean(tarifResponse) && tarifAttributes.length === 0;
    const attributes = usesFallbackFields ? FALLBACK_FIELDS : tarifAttributes;
    const raeume = useMemo(() => normalizeRaeume(raeumeResponse), [raeumeResponse]);
    const selectedRaeume = useMemo(
        () => raeume.filter((raum) => selectedRaumIds.includes(String(raum.id))),
        [raeume, selectedRaumIds],
    );
    const selectedTemplate = useMemo(
        () => templatesState.candidates.find((item) => templateKey(item) === selectedTemplateKey) || null,
        [templatesState.candidates, selectedTemplateKey],
    );

    const applyTemplate = async (template) => {
        setSelectedTemplateKey(templateKey(template));
        if (!template) {
            setTemplatePrefill(null);
            return;
        }
        try {
            setCopyLoading(true);
            const prefill = await apiService.getGemaTemplateCopy(
                template.lizenz_id,
                template.position,
                selectedEvent?.event_id,
                selectedEvent?.occurrence_id,
            );
            setTemplatePrefill(prefill || {});
            const tarifId = prefill?.tarif_id || template.tarif_id || "";
            const nutzungsortId = prefill?.nutzungsort_id || template.nutzungsort_id || "";
            setDraftTarifId(tarifId);
            setDraftNutzungsortId(nutzungsortId);
            setAppliedIds({ tarifId, nutzungsortId });
            if (!tarifId || !nutzungsortId) {
                toast.warning("Die Vorlage enthält keine Tarif- oder Nutzungsort-ID. Bitte manuell ergänzen.");
            }
        } catch (error) {
            console.error("Error copying GEMA template:", error);
            toast.error(error.response?.data?.detail || "Vorlage konnte nicht übernommen werden");
        } finally {
            setCopyLoading(false);
        }
    };

    // Beim Öffnen: Termin-Daten setzen, IDs vom Event nutzen oder Vorlagen vorschlagen.
    useEffect(() => {
        if (!open || !selectedEvent) {
            return;
        }
        setPhase(1);
        setCalcError("");
        setCalculationResponse(null);
        setContact(null);
        setContactError("");
        setAnzahlSetlists("");
        setMeldenPreview(null);
        setMeldenPreviewError("");
        setConfirmed(false);
        setTemplatePrefill(null);
        setSelectedTemplateKey("");
        setTarifResponse(null);
        setRaeumeResponse(null);
        setSelectedRaumIds([]);
        setEventName(selectedEvent.event_title || "");
        setFormData({
            beginDate: selectedEvent.event_date || "",
            startTime: normalizeTime(selectedEvent.event_time),
            endTime: normalizeTime(selectedEvent.event_end_time) || "",
        });
        setDraftTarifId(eventIds.tarifId);
        setDraftNutzungsortId(eventIds.nutzungsortId);
        setAppliedIds(hasEventIds ? { ...eventIds } : { tarifId: "", nutzungsortId: "" });

        if (hasEventIds) {
            setTemplatesState({ loading: false, error: "", candidates: [], suggestion: null, reason: null });
            return;
        }
        let active = true;
        const loadTemplates = async () => {
            setTemplatesState({ loading: true, error: "", candidates: [], suggestion: null, reason: null });
            try {
                const result = await apiService.getGemaTemplates(selectedEvent.event_id, selectedEvent.occurrence_id);
                if (!active) {
                    return;
                }
                setTemplatesState({
                    loading: false,
                    error: "",
                    candidates: Array.isArray(result?.candidates) ? result.candidates : [],
                    suggestion: result?.suggestion || null,
                    reason: result?.reason || null,
                });
                if (result?.suggestion) {
                    applyTemplate(result.suggestion);
                }
            } catch (error) {
                if (active) {
                    setTemplatesState({
                        loading: false,
                        error: error.response?.data?.detail || "Vorlagen konnten nicht geladen werden",
                        candidates: [],
                        suggestion: null,
                        reason: null,
                    });
                }
            }
        };
        loadTemplates();
        return () => {
            active = false;
        };
    }, [open, selectedEvent]);

    // Tarif-Merkmale und Räume laden, sobald Tarif und Nutzungsort feststehen.
    useEffect(() => {
        if (!open || !appliedIds.tarifId || !appliedIds.nutzungsortId) {
            return;
        }
        let active = true;
        const load = async () => {
            setLoadingTarif(true);
            setCalculationResponse(null);
            try {
                const [tarif, raeumePayload] = await Promise.all([
                    apiService.getGemaTarif(appliedIds.tarifId, selectedEvent?.event_id, formData.beginDate),
                    apiService.getGemaRaeume(appliedIds.nutzungsortId),
                ]);
                if (!active) {
                    return;
                }
                setTarifResponse(tarif || {});
                setRaeumeResponse(raeumePayload || {});

                const merkmale = templatePrefill?.dynamische_merkmale || {};
                const loadedAttributes = normalizeAttributes(tarif || {});
                const fields = loadedAttributes.length > 0 ? loadedAttributes : FALLBACK_FIELDS;
                // Reihenfolge: Wert aus dem KuBu-Event (vom Backend berechnet) > Vorlage > Standard des Tarifs.
                // Die Vorlage stammt von einer anderen Veranstaltung und wird daher immer überschrieben.
                const sources = {};
                setFormData((prev) => {
                    const next = { beginDate: prev.beginDate, startTime: prev.startTime, endTime: prev.endTime };
                    fields.forEach((attribute) => {
                        const kubuValue = attribute.raw?.kubu_value;
                        if (kubuValue !== undefined && kubuValue !== null && kubuValue !== "") {
                            next[attribute.key] = String(kubuValue);
                            sources[attribute.key] = "kubu";
                            return;
                        }
                        const fromTemplate = pickMerkmalValue(merkmale, attribute);
                        if (fromTemplate !== undefined) {
                            next[attribute.key] = fromTemplate;
                            sources[attribute.key] = attribute.raw?.kubu_missing ? "fehlt" : "vorlage";
                            return;
                        }
                        if (attribute.defaultValue !== undefined && attribute.defaultValue !== null) {
                            next[attribute.key] = attribute.defaultValue;
                            sources[attribute.key] = attribute.raw?.kubu_missing ? "fehlt" : "standard";
                            return;
                        }
                        sources[attribute.key] = "fehlt";
                    });
                    return next;
                });
                setPrefillSource(sources);

                const availableRaeume = normalizeRaeume(raeumePayload || {});
                const templateRaeume = Array.isArray(templatePrefill?.raeume) ? templatePrefill.raeume : [];
                const preselected = availableRaeume
                    .filter((raum) => templateRaeume.some((item) => (
                        String(item.id) === String(raum.id)
                        || String(item.name || "").toLowerCase() === String(raum.name || "").toLowerCase()
                    )))
                    .map((raum) => String(raum.id));
                setSelectedRaumIds(
                    preselected.length > 0
                        ? preselected
                        : (availableRaeume.length === 1 ? [String(availableRaeume[0].id)] : []),
                );
            } catch (error) {
                console.error("Error loading GEMA wizard metadata:", error);
                toast.error(error.response?.data?.detail || "GEMA Tarifdaten konnten nicht geladen werden");
            } finally {
                if (active) {
                    setLoadingTarif(false);
                }
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [open, appliedIds, templatePrefill, selectedEvent]);

    const updateFormValue = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const toggleRaum = (raumId) => {
        setSelectedRaumIds((prev) => (
            prev.includes(raumId) ? prev.filter((id) => id !== raumId) : [...prev, raumId]
        ));
    };

    const buildDynamischeMerkmale = () => {
        const merkmale = {};
        attributes.forEach((attribute) => {
            const value = formData[attribute.key];
            if (value === null || value === undefined || value === "") {
                return;
            }
            merkmale[attribute.sourceKey || attribute.key] = value;
        });
        if (selectedRaumIds.length > 0) {
            merkmale.raumIds = selectedRaumIds;
        }
        return merkmale;
    };

    const handleRememberTemplate = async () => {
        const category = selectedEvent?.event_category;
        if (!category || !selectedTemplate) {
            return;
        }
        try {
            setSavingRule(true);
            await apiService.saveGemaTemplateRule(category, selectedTemplate.title);
            toast.success(`Für „${category}“ wird künftig „${selectedTemplate.title}“ vorgeschlagen`);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Zuordnung konnte nicht gespeichert werden");
        } finally {
            setSavingRule(false);
        }
    };

    const handleCalculate = async () => {
        if (!formData.beginDate || !formData.startTime || !formData.endTime) {
            setCalcError("Datum, Beginn und Ende sind erforderlich.");
            return;
        }
        // Die GEMA rechnet nur mit vollständigen Merkmalen - leere Felder vorher abfragen.
        const missingRequired = attributes.filter((attribute) => (
            formData[attribute.key] === undefined || formData[attribute.key] === null || formData[attribute.key] === ""
        ));
        if (missingRequired.length > 0) {
            setCalcError(`Bitte ausfüllen: ${missingRequired.map((attribute) => attribute.label).join(", ")}`);
            return;
        }
        if (needsSetlists && !(Number(anzahlSetlists) >= 1)) {
            setCalcError("Bitte die Anzahl der Auftritte (Setlists) angeben.");
            return;
        }
        try {
            setCalculating(true);
            setCalcError("");
            const response = await apiService.calculateGemaTarif(appliedIds.tarifId, {
                dynamischeMerkmale: buildDynamischeMerkmale(),
                beginDate: formData.beginDate,
                startTime: formData.startTime,
                endTime: formData.endTime,
            });
            setCalculationResponse(response || {});
            setPhase(2);
        } catch (error) {
            console.error("Error calculating GEMA tariff:", error);
            setCalcError(error.response?.data?.detail || "Preisberechnung fehlgeschlagen");
        } finally {
            setCalculating(false);
        }
    };

    const buildMeldenPayload = () => ({
        event_id: selectedEvent?.event_id,
        occurrence_id: selectedEvent?.occurrence_id,
        tarif_id: appliedIds.tarifId,
        nutzungsort_id: appliedIds.nutzungsortId,
        template_lizenz_id: selectedTemplate?.lizenz_id || null,
        template_position: selectedTemplate?.position || null,
        event_bezeichnung: eventName,
        begin_date: formData.beginDate,
        start_time: formData.startTime,
        end_time: formData.endTime,
        dynamische_merkmale: buildDynamischeMerkmale(),
        raeume: selectedRaeume.map((raum) => raum.raw),
        anzahl_setlists: needsSetlists ? (Number(anzahlSetlists) || null) : null,
    });

    const handleTarifSuche = async () => {
        const query = tarifSuche.query.trim();
        if (query.length < 2) {
            return;
        }
        setTarifSuche((prev) => ({ ...prev, loading: true, error: "", results: null }));
        try {
            const result = await apiService.searchGemaTarife(query);
            setTarifSuche((prev) => ({
                ...prev,
                loading: false,
                results: Array.isArray(result?.tarife) ? result.tarife : [],
                defaultOrt: result?.default_nutzungsort_id || "",
            }));
        } catch (error) {
            setTarifSuche((prev) => ({ ...prev, loading: false, error: error.response?.data?.detail || "Tarifsuche fehlgeschlagen" }));
        }
    };

    const chooseTarifWithoutTemplate = (tarif) => {
        // Fall ohne Vorlage: Merkmale kommen nur aus dem KuBu-Event bzw. werden hier abgefragt.
        setSelectedTemplateKey("");
        setTemplatePrefill(null);
        const nutzungsortId = draftNutzungsortId.trim() || tarifSuche.defaultOrt;
        setDraftTarifId(tarif.tarif_id);
        setDraftNutzungsortId(nutzungsortId);
        setAppliedIds({ tarifId: tarif.tarif_id, nutzungsortId });
    };

    const goToSummary = async () => {
        setPhase(3);
        setConfirmed(false);
        // Zeigt den Body, der an tarif/melden ginge (Backend rechnet den Preis neu, sendet nichts Verbindliches).
        setMeldenPreview(null);
        setMeldenPreviewError("");
        apiService.previewGemaMeldung(buildMeldenPayload())
            .then((preview) => setMeldenPreview(preview || {}))
            .catch((error) => setMeldenPreviewError(error.response?.data?.detail || "Melde-Vorschau fehlgeschlagen"));
        if (contact) {
            return;
        }
        try {
            setContactError("");
            setContact(await apiService.getGemaCheckoutPreview(
                templatePrefill?.rechnungsadresse_id,
                templatePrefill?.bankdaten_id,
            ));
        } catch (error) {
            setContactError(error.response?.data?.detail || "Kontaktdaten konnten nicht geladen werden");
        }
    };

    const handleSubmit = async () => {
        if (!canOrder || !confirmed || submitting) {
            return;
        }
        try {
            setSubmitting(true);
            const result = await apiService.submitGemaMeldung({ ...buildMeldenPayload(), confirmed: true });
            if (result?.status === "unbekannt") {
                toast.warning(result.message, { duration: 20000 });
            } else {
                toast.success(result?.message || "Anmeldung bei der GEMA eingegangen");
            }
            onOpenChange(false);
            if (onSubmitted) {
                onSubmitted();
            }
        } catch (error) {
            console.error("Error submitting GEMA report:", error);
            toast.error(error.response?.data?.detail || "GEMA-Meldung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    const isVoranmeldung = meldenPreview?.nutzungType === "VORANMELDUNG";
    const canOrder = GEMA_ORDER_ENABLED && Boolean(meldenPreview) && !meldenPreviewError;
    const preispositionen = extractPreispositionen(calculationResponse);
    const bruttoGesamt = resolveBruttoGesamt(calculationResponse);
    const canCalculate = Boolean(appliedIds.tarifId && appliedIds.nutzungsortId) && !loadingTarif && !copyLoading;
    const idsChanged = draftTarifId.trim() !== appliedIds.tarifId || draftNutzungsortId.trim() !== appliedIds.nutzungsortId;
    const merkmalLabel = (attribute) => {
        const value = formData[attribute.key];
        const option = attribute.options?.find((item) => String(item.value) === String(value));
        return option?.label || value;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Bei der GEMA anmelden</DialogTitle>
                    <DialogDescription>
                        {selectedEvent?.event_title || "Veranstaltung"} ({formatDateDe(selectedEvent?.event_date)}) – Phase {phase} von 3
                    </DialogDescription>
                </DialogHeader>

                {phase === 1 && (
                    <div className="space-y-4">
                        {!hasEventIds && (
                            <div className="space-y-2 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                                <p className="text-sm font-semibold text-indigo-900">Vorlage („Anmeldung kopieren“)</p>
                                {templatesState.loading && <p className="text-sm text-indigo-800">Frühere Anmeldungen werden geladen...</p>}
                                {templatesState.error && <p className="text-sm text-red-700">{templatesState.error}</p>}
                                {!templatesState.loading && !templatesState.error && (
                                    <>
                                        {templatesState.suggestion ? (
                                            <p className="text-xs text-indigo-800">
                                                Vorschlag: „{templatesState.suggestion.title}“ vom {formatDateDe(templatesState.suggestion.date)}
                                                {templatesState.reason ? ` (${templatesState.reason})` : ""}
                                            </p>
                                        ) : (
                                            <p className="text-xs font-semibold text-amber-800">
                                                Keine passende Vorlage gefunden. Bitte eine Vorlage wählen oder Tarif-/Nutzungsort-ID manuell eintragen.
                                            </p>
                                        )}
                                        <select
                                            value={selectedTemplateKey}
                                            onChange={(event) => applyTemplate(
                                                templatesState.candidates.find((item) => templateKey(item) === event.target.value) || null,
                                            )}
                                            disabled={copyLoading}
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                        >
                                            <option value="">– keine Vorlage –</option>
                                            {templatesState.candidates.map((item) => (
                                                <option key={templateKey(item)} value={templateKey(item)}>
                                                    {item.title} ({formatDateDe(item.date)}, Lizenz {item.lizenz_id}{item.position ? `/${item.position}` : ""})
                                                    {item.gema_status ? ` – ${item.gema_status}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                        {copyLoading && <p className="text-xs text-indigo-800">Vorlage wird übernommen...</p>}
                                        {selectedTemplate
                                            && templateKey(selectedTemplate) !== templateKey(templatesState.suggestion)
                                            && selectedEvent?.event_category && (
                                            <button
                                                type="button"
                                                onClick={handleRememberTemplate}
                                                disabled={savingRule}
                                                className="text-xs font-semibold text-indigo-800 underline hover:no-underline disabled:opacity-60"
                                            >
                                                Für Kategorie „{selectedEvent.event_category}“ künftig diese Vorlage vorschlagen
                                            </button>
                                        )}
                                    </>
                                )}
                                <div className="mt-2 space-y-2 border-t border-indigo-200 pt-3">
                                    <p className="text-xs font-semibold text-indigo-900">Ohne Vorlage: Tarif über Stichwort suchen</p>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={tarifSuche.query}
                                            onChange={(event) => setTarifSuche((prev) => ({ ...prev, query: event.target.value }))}
                                            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); handleTarifSuche(); } }}
                                            placeholder="z. B. Ausstellung, Konzert, Party"
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleTarifSuche}
                                            disabled={tarifSuche.loading || tarifSuche.query.trim().length < 2}
                                            className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-60"
                                        >
                                            {tarifSuche.loading ? "Suche..." : "Suchen"}
                                        </button>
                                    </div>
                                    {tarifSuche.error && <p className="text-xs text-red-700">{tarifSuche.error}</p>}
                                    {tarifSuche.results && tarifSuche.results.length === 0 && (
                                        <p className="text-xs text-amber-800">Kein Tarif zu diesem Stichwort (das Portal kennt z. B. „Ausstellung“, aber nicht „Vernissage“).</p>
                                    )}
                                    {(tarifSuche.results || []).map((tarif) => (
                                        <button
                                            key={tarif.tarif_id}
                                            type="button"
                                            onClick={() => chooseTarifWithoutTemplate(tarif)}
                                            className={`block w-full rounded-lg border px-3 py-2 text-left text-xs ${appliedIds.tarifId === tarif.tarif_id && !selectedTemplate ? "border-indigo-500 bg-white" : "border-gray-200 bg-white hover:border-indigo-300"}`}
                                        >
                                            <span className="font-semibold text-gray-900">{tarif.label} ({tarif.tarif_id})</span>
                                            {tarif.setlist && <span className="ml-2 text-indigo-700">Setlist</span>}
                                            {tarif.saetze.length > 0 && (
                                                <span className="mt-1 block text-gray-600">{tarif.saetze.join(" · ")}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid gap-3 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Tarif-ID</label>
                                <input
                                    type="text"
                                    value={draftTarifId}
                                    onChange={(event) => setDraftTarifId(event.target.value)}
                                    placeholder="z. B. T001205"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Nutzungsort-ID</label>
                                <input
                                    type="text"
                                    value={draftNutzungsortId}
                                    onChange={(event) => setDraftNutzungsortId(event.target.value)}
                                    placeholder="z. B. 4001608540"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>
                        </div>
                        {idsChanged && (
                            <button
                                type="button"
                                onClick={() => setAppliedIds({ tarifId: draftTarifId.trim(), nutzungsortId: draftNutzungsortId.trim() })}
                                disabled={!draftTarifId.trim() || !draftNutzungsortId.trim()}
                                className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                Tarif und Räume laden
                            </button>
                        )}

                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Name der Veranstaltung</label>
                            <input
                                type="text"
                                value={eventName}
                                onChange={(event) => setEventName(event.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                        </div>

                        <div className="grid gap-3 md:grid-cols-3">
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Datum</label>
                                <input type="date" value={formData.beginDate || ""} onChange={(event) => updateFormValue("beginDate", event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Beginn</label>
                                <input type="time" value={formData.startTime || ""} onChange={(event) => updateFormValue("startTime", event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Ende</label>
                                <input type="time" value={formData.endTime || ""} onChange={(event) => updateFormValue("endTime", event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                            </div>
                        </div>

                        {loadingTarif && (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">Tarif und Räume werden geladen...</div>
                        )}

                        {!loadingTarif && appliedIds.nutzungsortId && (
                            <div>
                                <p className="mb-1 text-xs font-semibold uppercase text-gray-500">Räume</p>
                                {raeume.length === 0 ? (
                                    <p className="text-sm text-amber-700">Für diesen Nutzungsort wurden keine Räume gefunden.</p>
                                ) : (
                                    <div className="flex flex-wrap gap-4">
                                        {raeume.map((raum) => (
                                            <label key={raum.id} className="inline-flex items-center gap-2 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRaumIds.includes(String(raum.id))}
                                                    onChange={() => toggleRaum(String(raum.id))}
                                                />
                                                {raum.name}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {needsSetlists && (
                        <div>
                            <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Anzahl Auftritte (Setlists) *</label>
                            <input
                                type="number"
                                min="1"
                                max="999"
                                value={anzahlSetlists}
                                onChange={(event) => setAnzahlSetlists(event.target.value)}
                                className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            />
                            <p className="mt-1 text-xs text-gray-500">Eine Setlist je Band/Act – wird vom KuBu-Formular nicht abgefragt.</p>
                        </div>
                        )}

                        {!loadingTarif && attributes.length > 0 && (
                            <div className="space-y-3 rounded-lg border border-gray-200 p-4">
                                <p className="text-sm font-semibold text-gray-800">Tariffragen</p>
                                {usesFallbackFields && (
                                    <p className="text-xs text-amber-700">
                                        Der Tarif lieferte keine Merkmale, daher Standardfelder. Feldnamen sind vorläufig.
                                    </p>
                                )}
                                {attributes.map((attribute) => (
                                    <div key={attribute.key} className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            {attribute.label}
                                            {attribute.required ? " *" : ""}
                                        </label>
                                        {attribute.info && <p className="text-xs text-gray-500">{attribute.info}</p>}
                                        {prefillSource[attribute.key] === "kubu" && (
                                            <p className="text-xs text-green-700">Aus dem KuBu-Event ({attribute.raw?.kubu_label}).</p>
                                        )}
                                        {prefillSource[attribute.key] === "vorlage" && (
                                            <p className="text-xs text-amber-700">Aus der Vorlage übernommen – bitte prüfen.</p>
                                        )}
                                        {prefillSource[attribute.key] === "fehlt" && (
                                            <p className="text-xs font-semibold text-red-700">
                                                {attribute.raw?.kubu_label
                                                    ? `Fehlt im KuBu-Event („${attribute.raw.kubu_label}“) – bitte hier angeben und im Event nachtragen.`
                                                    : "Wird vom KuBu-Formular nicht abgefragt – bitte hier angeben."}
                                                {formData[attribute.key] ? " Vorgeschlagener Wert aus Vorlage/Tarif." : ""}
                                            </p>
                                        )}
                                        <AttributeInput
                                            attribute={attribute}
                                            value={formData[attribute.key]}
                                            onChange={(value) => updateFormValue(attribute.key, value)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {calcError && (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                Preisberechnung nicht möglich: {calcError}
                            </div>
                        )}
                    </div>
                )}

                {phase === 2 && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 p-4">
                            <p className="mb-2 text-sm font-semibold text-gray-800">Preisberechnung</p>
                            <div className="space-y-2">
                                {preispositionen.map((position, index) => (
                                    <div key={`preis_${index}`} className="flex items-center justify-between text-sm text-gray-700">
                                        <span>{position?.bezeichnung || position?.titel || `Position ${index + 1}`}</span>
                                        <span className={["NET", "BRUT"].includes(position?.typKennung) ? "font-semibold" : ""}>
                                            {formatMoney(position?.wert)}
                                        </span>
                                    </div>
                                ))}
                                {preispositionen.length === 0 && (
                                    <p className="text-sm text-gray-500">Keine Preispositionen in der Antwort gefunden.</p>
                                )}
                                <div className="mt-3 border-t border-gray-200 pt-3 text-base font-bold text-gray-900">
                                    Brutto-Gesamtbetrag: {formatMoney(bruttoGesamt)}
                                </div>
                            </div>
                        </div>
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            Vorläufiger Preis. Die GEMA kann nach Prüfung abweichen (z. B. 40,17 € berechnet, 50,20 € in Rechnung gestellt).
                        </div>
                    </div>
                )}

                {phase === 3 && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 p-4">
                            <p className="mb-2 text-sm font-semibold text-gray-800">Zusammenfassung</p>
                            <div className="grid gap-2 text-sm text-gray-700 md:grid-cols-2">
                                <p><span className="font-semibold">Veranstaltung:</span> {eventName || "-"}</p>
                                <p><span className="font-semibold">Datum:</span> {formatDateDe(formData.beginDate)}, {formData.startTime || "-"}–{formData.endTime || "-"}</p>
                                <p><span className="font-semibold">Tarif-ID:</span> {appliedIds.tarifId}</p>
                                <p><span className="font-semibold">Nutzungsort-ID:</span> {appliedIds.nutzungsortId}</p>
                                <p><span className="font-semibold">Räume:</span> {selectedRaeume.map((raum) => raum.name).join(", ") || "-"}</p>
                                <p><span className="font-semibold">Vorläufiger Preis:</span> {formatMoney(bruttoGesamt)}</p>
                                {selectedTemplate && (
                                    <p className="md:col-span-2">
                                        <span className="font-semibold">Vorlage:</span> „{selectedTemplate.title}“ vom {formatDateDe(selectedTemplate.date)}
                                        {" "}(Lizenz {selectedTemplate.lizenz_id}{selectedTemplate.position ? ` / Pos. ${selectedTemplate.position}` : ""})
                                    </p>
                                )}
                            </div>
                            <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm text-gray-700">
                                {attributes.filter((attribute) => formData[attribute.key] !== undefined && formData[attribute.key] !== "").map((attribute) => (
                                    <p key={attribute.key}><span className="font-semibold">{attribute.label}:</span> {merkmalLabel(attribute)}</p>
                                ))}
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 p-4">
                            <p className="mb-2 text-sm font-semibold text-gray-800">Rechnungs- und Kontaktdaten (aus dem GEMA-Portal, nur Anzeige)</p>
                            {contactError && <p className="text-sm text-red-700">{contactError}</p>}
                            {!contact && !contactError && <p className="text-sm text-gray-500">Wird geladen...</p>}
                            {contact && (
                                <div className="grid gap-1 text-sm text-gray-700 md:grid-cols-2">
                                    <p>{contact.name || "-"}</p>
                                    <p>{[contact.strasse, contact.hausnummer].filter(Boolean).join(" ")}, {[contact.plz, contact.ort].filter(Boolean).join(" ")}</p>
                                    <p>Rechnungs-Mail: {contact.rechnungs_email || "-"}</p>
                                    <p>Telefon: {contact.telefon || "-"}</p>
                                    <p>Zahlungsart: {contact.zahlungsart || "-"}{contact.bank ? `, ${contact.bank}` : ""}{contact.iban ? ` (${contact.iban})` : ""}</p>
                                </div>
                            )}
                        </div>

                        <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
                            <p className="mb-1 font-semibold text-gray-800">Melde-Daten für die GEMA (Vorschau, geschwärzt)</p>
                            {meldenPreviewError && <p className="text-red-700">{meldenPreviewError}</p>}
                            {!meldenPreview && !meldenPreviewError && <p className="text-gray-500">Wird vorbereitet...</p>}
                            {meldenPreview && (
                                <>
                                    <p>
                                        Art: {meldenPreview.nutzungType === "NACHMELDUNG" ? "Nachmeldung" : meldenPreview.nutzungType === "VORANMELDUNG" ? "Voranmeldung" : "Anmeldung"}
                                        {" · "}Preis laut GEMA: {formatMoney(meldenPreview.gesamtPreis)}
                                    </p>
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-xs text-gray-500">Request-Body anzeigen</summary>
                                        <pre className="mt-2 max-h-64 overflow-auto rounded bg-gray-50 p-2 text-xs">{JSON.stringify(meldenPreview.body, null, 2)}</pre>
                                    </details>
                                </>
                            )}
                        </div>

                        {isVoranmeldung && (
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
                                <p className="font-semibold">Voranmeldung</p>
                                <p>
                                    Die GEMA rechnet vorläufig mit Sitzplätzen/Kapazität. Nach der Veranstaltung fordert sie die
                                    tatsächlichen Daten an („Daten ergänzen“ in Meine Veranstaltungen).
                                </p>
                            </div>
                        )}

                        <label className="flex items-start gap-2 text-sm text-gray-800">
                            <input
                                type="checkbox"
                                checked={confirmed}
                                disabled={!canOrder}
                                onChange={(event) => setConfirmed(event.target.checked)}
                                className="mt-1"
                            />
                            Ich bestelle diese Einzellizenz zahlungspflichtig bei der GEMA (Lastschrift vom hinterlegten Konto).
                        </label>
                    </div>
                )}

                <DialogFooter>
                    {phase > 1 && (
                        <button
                            type="button"
                            onClick={() => setPhase(phase - 1)}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Zurück
                        </button>
                    )}
                    {phase === 1 && (
                        <button
                            type="button"
                            onClick={handleCalculate}
                            disabled={calculating || !canCalculate}
                            className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                        >
                            {calculating ? "Berechne..." : "Preis berechnen & Vorschau"}
                        </button>
                    )}
                    {phase === 2 && (
                        <button
                            type="button"
                            onClick={goToSummary}
                            className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
                        >
                            Weiter zur Zusammenfassung
                        </button>
                    )}
                    {phase === 3 && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!canOrder || !confirmed || submitting}
                            title={canOrder ? undefined : "Erst möglich, wenn die Melde-Vorschau fehlerfrei geladen ist"}
                            className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-60"
                        >
                            {submitting ? "Bestelle..." : "Zahlungspflichtig bestellen"}
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default GemaSubmissionWizard;
